<template>
    <div class="panel__opened">
        <div @mousemove="setCurrentHovered">
            <ul class="outlookSearchResults scrollBox pos-static outlookSearchResults--small">
                <li v-once>
                    <h2 class="marketingHubPage--quickStart__header text-color--dark-blue-gray">
                        {{ generateMarketName(currentOutlook) }} {{ trans('XMO_HIGHLIGHTS_SEARCH_RESULTS_TOPIC_HEADLINE') }}
                    </h2>
                </li>
                <li>
                    <span
                        v-if="!matchesCurrentOutlook.length > 0"
                        class="marketingHubPageAutoComplete--resultsItem">
                        <p class="margin-horizontal-20 margin-bottom-30">
                            {{ trans('XMO_HIGHLIGHTS_SEARCH_NO_RESULTS_CURRENT_MARKET') }}
                        </p>
                    </span>
                </li>
                <template v-if="matchedSearchResults.length && matchesCurrentOutlook.length">
                    <li
                        v-for="(match, index) in matchesCurrentOutlook"
                        :key="`${ match.product }--${ index }`"
                        ref="results"
                        class="marketingHubPageAutoComplete--resultsItem outlookMarketingHub__icons flex flex-center quickStart__item"
                        @keyup.tab="changeProduct(match)"
                        @mousemove="setTabActive(match, $refs.results, $event)">
                        <!-- Left Section -->
                        <MatchResult
                            :match="match"
                            :match-market-link="generateMarketLink(match.url, match.slug)"
                            :gtm="`xmoHighlights__matchedResult--${ sanitizeDataGtm(match.market) }`"
                            :selected-product="selectedProduct"
                            :icon-class="generateIconClass(match)"
                            :market-name="generateMarketName(match.product)"
                            :svg-icon="getSvgIcon(match.product)" />
                        <!-- Right Section -->
                        <MatchOverview
                            v-show="selectedProduct.uid === match.uid"
                            :market-name="generateMarketName(selectedProduct.product)"
                            :market="selectedProduct.market"
                            :description="formatDescription(selectedProduct.description)"
                            :market-link="generateMarketLink(selectedProduct.url, selectedProduct.slug)"
                            :gtm="`xmoHighlights__matchedResultOverview--${ sanitizeDataGtm(match.market) }`" />
                    </li>
                </template>
                <!-- Matches other Outlooks -->
                <template v-if="matchedSearchResults.length && matchesOtherOutlooks.length">
                    <li v-once>
                        <span>
                            <h2 class="marketingHubPage--quickStart__header text-color--dark-blue-gray margin-horizontal-20 margin-bottom-30">
                                {{ trans('XMO_HIGHLIGHTS_SEARCH_RESULTS_OTHER_MARKET') }}
                            </h2>
                        </span>
                    </li>
                    <li
                        v-for="(match, index) in matchesOtherOutlooks"
                        :key="`${ match.product }--${ index }`"
                        ref="results"
                        class="marketingHubPageAutoComplete--resultsItem outlookMarketingHub__icons flex flex-center quickStart__item"
                        @keyup.tab="changeProduct(match)"
                        @mousemove="setTabActive(match, $refs.results, $event)">
                        <!-- Left Section -->
                        <MatchResult
                            :match="match"
                            :match-market-link="generateMarketLink(match.url, match.slug)"
                            :gtm="`xmoHighlights__matchedResult--${ sanitizeDataGtm(match.market) }`"
                            :icon-class="generateIconClass(match)"
                            :market-name="generateMarketName(match.product)"
                            :svg-icon="getSvgIcon(match.product)" />
                        <!-- Right Section -->
                        <MatchOverview
                            v-show="selectedProduct.uid === match.uid"
                            :market-name="generateMarketName(selectedProduct.product)"
                            :market="selectedProduct.market"
                            :description="formatDescription(selectedProduct.description)"
                            :market-link="generateMarketLink(selectedProduct.url, selectedProduct.slug)"
                            :gtm="`xmoHighlights__matchedResultOverview--${ sanitizeDataGtm(match.market) }`" />
                    </li>
                </template>
            </ul>
            <div
                v-if="showAiResults"
                data-fb-container="xmoAiSearchResults__feedback"
                :data-fb-request="searchInput"
                :data-fb-response="JSON.stringify(aiSearchResults)">
                <!-- FeedbackBanner -->
            </div>
            <!-- Footer -->
            <template>
                <div
                    v-if="showAiResults"
                    class="xmoAiLoadingBannerFooter">
                    <p>
                        {{ trans('XMO_POWERED_BY') }}
                        <span class="xmoAiLoadingBannerFooter__aiText">
                            {{ trans('OPEN_AI') }}
                        </span>
                    </p>
                </div>
            </template>
            <div
                v-once
                class="highlights--footer linkArrowHover">
                {{ trans('XMO_HIGHLIGHTS_UNDERSTAND_MARKETS') }}
                <a
                    :href="getPath('outlookHubPage')"
                    role="menuitem"
                    data-gtm="xmoHighlights__mainMarketsLink">
                    {{ trans('XMO_HIGHLIGHTS_VISIT_MARKET') }}
                    <svg
                        aria-hidden="true"
                        class="stat__svgIcon margin-left-5">
                        <use href="#iconArrowRightLongBold" />
                    </svg>
                </a>
            </div>
        </div>
    </div>
</template>
<script>
    import MatchOverview from 'outlook/components/marketHighlights/matchOverview';
    import MatchResult from 'outlook/components/marketHighlights/matchResult';
    import { outlookSearchStore } from 'outlook/store/outlookSearchStore';
    import { generateMarketName } from 'outlook/utils/generateMarketName';
    import { getFromTemplate, getPath } from 'tools/getFromHtml';
    import { activateTab, setCurrentHovered } from 'tools/mouseoverAxis';
    import Translator from 'tools/trans';
    import { mapState } from 'vuex';

    // There is not a specific length decided at the moment this should be changed once max length is decided.
    const MAXIMUM_TEXT_LENGTH = 400;
    const FALLBACK_SVG_ICON = 'fallbackIcon';

    export default {
        components: {
            MatchResult,
            MatchOverview,
        },
        store: outlookSearchStore,
        props: {
            matchedSearchResults: { type: Array, required: true },
            icons: { type: Object, required: true },
        },
        data() {
            return {
                selectedProduct: this.matchedSearchResults[0],
                currentUid: null,
                elementBaseClass: 'autocompleteList__results--svg',
                iconClassName: '',
                svgBaseClass: 'outlookClusterIcon outlookClusterIcon--',
                outlookBasePath: getPath('outlookHubPage'),
                currentOutlook: getFromTemplate('var', 'outlookName'),
            };
        },
        computed: {
            ...mapState([
                'searchInput',
                'showAiResults',
                'aiSearchResults',
            ]),
            matchesCurrentOutlook() {
                return this.matchedSearchResults.filter(market => market.product === this.currentOutlook)
                    .map((result) => {
                        return { ...result, uid: this.getUniqueId(result) };
                    });
            },
            matchesOtherOutlooks() {
                return this.matchedSearchResults.filter(market => market.product !== this.currentOutlook)
                    .map(result => {
                        return { ...result, uid: this.getUniqueId(result) };
                    });
            },
        },
        watch: {
            matchesCurrentOutlook() {
                this.selectedProduct = this.matchesCurrentOutlook[0];
            },
            matchesOtherOutlooks() {
                this.selectedProduct = this.matchesOtherOutlooks[0];
            },
        },
        methods: {
            trans: Translator.trans,
            activateTab,
            generateMarketName,
            setCurrentHovered,
            getPath,
            getUniqueId(match) {
                const { uid, id } = match;
                const uidString = `${ uid }${ id }`;

                return parseInt(uidString);
            },
            generateMarketLink(url, slug) {
                return `${ this.outlookBasePath }${ url }/${ slug }`;
            },
            setTabActive(match, ref, event) {
                const { uid } = match;
                this.currentUid = this.activateTab(uid, ref, event);
                if (uid === this.currentUid) {
                    this.selectedProduct = [...this.matchesCurrentOutlook, ...this.matchesOtherOutlooks]
                        .find((prod) => prod.uid === this.currentUid);

                    return this.selectedProduct;

                }

                return this.selectedProduct;
            },
            changeProduct(match) {
                this.selectedProduct = match;
            },
            formatDescription(description = '') {
                if (description.length > MAXIMUM_TEXT_LENGTH) {
                    return `${ description.substring(0, MAXIMUM_TEXT_LENGTH).trim() }...`;
                }

                return description;
            },
            generateIconClass(match) {
                if (match.uid === this.selectedProduct.uid) {
                    return `${ this.svgBaseClass }${ match.product } ${ this.elementBaseClass }`;
                }

                return this.elementBaseClass;
            },
            sanitizeDataGtm(target) {
                return target.replace(/[^\w\s]/gi, '').replace(/ /g, '-');
            },
            getSvgIcon(match) {
                if (this.icons[match]) {
                    return this.icons[match].path;
                }

                return this.icons[FALLBACK_SVG_ICON].path;
            },
            setSelectedProduct(product) {
                this.selectedProduct = product[0];
            },
        },
        created() {
            if (this.matchesCurrentOutlook.length > 0) {
                return this.setSelectedProduct(this.matchesCurrentOutlook);
            }
            this.setSelectedProduct(this.matchesOtherOutlooks);
        },
    };
</script>
